import { motion } from "framer-motion";
import { useState } from "react";
import { playSfx, speak } from "@/lib/sounds";
import { useGame } from "@/context/GameContext";
import mascotHappy from "@/assets/mascot/mascot_happy.png";
import mascotThinking from "@/assets/mascot/mascot_thinking.png";
import mascotCelebrate from "@/assets/mascot/mascot_celebrate.png";
import mascotWave from "@/assets/mascot/mascot_wave.png";

const MASCOT_MAP = {
  happy: mascotHappy,
  thinking: mascotThinking,
  celebrate: mascotCelebrate,
  wave: mascotWave,
  default: mascotHappy,
};

// Age-appropriate interaction messages based on developmental research:
// 1yr: simple cause-effect (tap -> giggle)
// 2yr: imitation (tap -> baby waves, says basic word)
// 3yr: turn-taking (baby asks question)
// 4yr: encouragement & instruction
// 5yr: dialogue & praise
const buildInteractionMessages = (language) => ({
  1: ["Hihihi!", "Aaa-baa!", "Wow!", "Hihihi!"],
  2: [
    language === "id" ? "Halo!" : "Hi!",
    language === "id" ? "Main yuk!" : "Let's play!",
    language === "id" ? "Wah!" : "Wow!",
  ],
  3: [
    language === "id" ? "Mau main apa?" : "What shall we play?",
    language === "id" ? "Yuk bersama!" : "Let's play together!",
    language === "id" ? "Pintar!" : "Smart!",
  ],
  4: [
    language === "id" ? "Kamu bisa!" : "You can do it!",
    language === "id" ? "Coba ini!" : "Try this!",
    language === "id" ? "Bagus sekali!" : "Very good!",
  ],
  5: [
    language === "id" ? "Pikirkan baik-baik" : "Think carefully",
    language === "id" ? "Kamu hebat!" : "You are awesome!",
    language === "id" ? "Hampir benar!" : "Almost there!",
  ],
});

export default function Companion({ age, mood = "default", themeColor = "#38BDF8" }) {
  const { language, soundOn } = useGame();
  const [bouncing, setBouncing] = useState(false);
  const [message, setMessage] = useState("");

  const handleTap = () => {
    setBouncing(true);
    playSfx("giggle", soundOn);
    const messages = buildInteractionMessages(language);
    const msgs = messages[age] || messages[3];
    const msg = msgs[Math.floor(Math.random() * msgs.length)];
    setMessage(msg);
    if (age >= 2) speak(msg, language, soundOn);
    setTimeout(() => {
      setBouncing(false);
      setMessage("");
    }, 1800);
  };

  return (
    <div className="relative flex flex-col items-center select-none" data-testid="companion-baby">
      {message && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.7 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          className="mb-2 px-5 py-2 rounded-3xl bg-white shadow-lg font-display font-bold text-lg sm:text-xl"
          style={{ color: themeColor, border: `3px solid ${themeColor}` }}
          data-testid="companion-message"
        >
          {message}
        </motion.div>
      )}
      <motion.button
        type="button"
        onClick={handleTap}
        animate={bouncing ? { scale: [1, 1.15, 0.95, 1.05, 1], rotate: [0, -5, 5, -3, 0] } : { y: [0, -8, 0] }}
        transition={bouncing ? { duration: 0.7 } : { duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        whileTap={{ scale: 0.9 }}
        className="rounded-full p-1 toy-btn"
        data-testid="companion-tap-btn"
        aria-label="Tap baby"
      >
        <div
          className="rounded-full overflow-hidden border-[6px] shadow-2xl"
          style={{ borderColor: themeColor, background: "white" }}
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <img
              src={MASCOT_MAP[mood] || MASCOT_MAP.default}
              alt="Companion"
              className="w-full h-full object-contain drop-shadow-lg"
              draggable={false}
            />
          </motion.div>
        </div>
      </motion.button>
    </div>
  );
}
